/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — Provider Web
 * Purpose      : Wire types mirroring the provider-api DTOs, plus the pure derivations
 *                the screens display (channel/status labels, request type, UTC dates).
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * PR 7910 T59    | 04-Aug-2026 | Vijayakumar S  | SUPERSEDED added to TemplateStatus,
 *                                                 statusLabel, statusColor and statusOrder;
 *                                                 unknown statuses now sort LAST, not first
 * template order | 27-Aug-2026 | Claude         | Adds sortForManageList — the manage-templates
 *                                                 table's row order, newest FAMILY first with a
 *                                                 family's versions kept together. It does NOT
 *                                                 replace groupByChannelAndStatus, which is left
 *                                                 exactly as it was (and still tested): the two
 *                                                 answer different questions, and the (statusRank,
 *                                                 name) order buried every new DRAFT behind four
 *                                                 status blocks. See the helper's own javadoc
 * 30639          | 14-Sep-2026 | Shabnam        | Added business-flow template categories and
 *                                                 channel/category default selection for
 *                                                 Estimate Link templates.
 * template_kind  | 15-Sep-2026 | Claude         | Added TemplateKind + templateKind on
 *                                                 TemplateSummary and templateKindLabel. The DTO
 *                                                 has carried the field since V1_5_3_6_8; this
 *                                                 type simply had no name for it, so the Template
 *                                                 List could not tell a code-fired SYSTEM template
 *                                                 apart from an automation or manual one.
 */

/**
 * Mirrors `TemplateSummaryDTO` (provider-data, automation package) EXACTLY as the wire
 * delivers it. Fields the DTO does not carry are deliberately absent rather than
 * optional-and-hopeful. `submittedByTenant` is now carried ON THE REVIEW QUEUE (the backend
 * enrichment sets it from the change request's `submitted_by_tenant`); `submittedByUser` /
 * `submittedAt` still exist on the ENTITY but remain dropped by `MessageTemplateApi.toSummaryDTO`.
 *
 * `createdAt`/`createdBy`/`publishedAt` added for Version History (Comms PR-22) — the DTO
 * already carried `createdBy`/`publishedAt` (PR-04's foundation columns) and gained
 * `createdAt` in the same PR; this type had simply never been updated to match until now.
 * All three are OPTIONAL here (not on the wire, which always sends them) so every existing
 * `TemplateSummary` object literal in this codebase's tests keeps compiling unchanged —
 * widening a field from absent to optional is additive; making it required is not.
 */
export interface TemplateSummary {
  templateMasterUid: string;
  version: number;
  /** Raw CommunicationChannel code — 1 = EMAIL, 2 = SMS. Never the enum name. */
  channel: number;
  name: string;
  /** Email only; always null for SMS (DB CHECK chk_template_subject_by_channel). */
  subject: string | null;
  body: string;
  status: TemplateStatus;
  reviewedBy: string | null;
  /** ISO-8601 WITH offset, from an OffsetDateTime. Never hand-append or strip a `Z`. */
  reviewedAt: string | null;
  reviewNotes: string | null;
  /** ISO-8601 WITH offset. The wire always sends this — {@code created_at} is NOT NULL. */
  createdAt?: string;
  /** Who created this version row. Null on every row today — nothing populates it yet. */
  createdBy?: string | null;
  /** ISO-8601 WITH offset. Null until a version has been published (Comms PR-20's `publish()`). */
  publishedAt?: string | null;
  templateCategory?: string | null;
  /**
   * WHO/WHAT sends this template — see {@link TemplateKind}. OPTIONAL here for the same reason
   * `createdAt` above is: the wire always sends it (the column is NOT NULL, defaulting every
   * pre-V1_5_3_6_8 row to AUTOMATION), but widening it from absent to optional keeps every
   * existing `TemplateSummary` object literal in this codebase's tests compiling unchanged.
   */
  templateKind?: TemplateKind | null;
  isDefault?: boolean;
  /**
   * The kind of change request behind this row, when one exists — populated on the REVIEW QUEUE only
   * (the backend joins the change_request there). Lets the reviewer tell a DELETION_REQUEST apart from
   * a NEW_TEMPLATE/CHANGE_REQUEST, which are otherwise inferred from the version number. Undefined on
   * non-queue reads and on provider self-edits (no request).
   */
  requestKind?: string | null;
  /** The tenant's removal reason — set only when {@link requestKind} is `DELETION_REQUEST`. */
  requestReason?: string | null;
  /**
   * WHICH tenant raised the request behind this row — populated on the REVIEW QUEUE only, from the
   * change request's `submitted_by_tenant` provenance. A tenant schema-id string (e.g. `tn24047750`),
   * NOT a company name. Undefined on non-queue reads and on provider self-edits (no request).
   */
  submittedByTenant?: string | null;
}

export interface TemplateCategoryOption {
  code: string;
  label: string;
}

/** Mirrors `TemplateSubmitDTO`. `subject` MUST be omitted for SMS. */
export interface TemplateSubmit {
  templateMasterUid?: string | null;
  channel: number;
  name: string;
  subject?: string | null;
  body: string;
}

/**
 * The COMPLETE set of `MessageTemplateMaster.STATUS_*` constants — all eight. Anything
 * missing here is not merely unlabelled: it also falls out of `STATUS_ORDER` below and
 * sorts wrongly, which is how `SUPERSEDED` came to sort above `UNDER_REVIEW` (T59).
 *
 * `SUPERSEDED` was added server-side by `V1_5_0_0_80` (constraint A9 step 2): approving
 * version N+1 now DEMOTES the outgoing live version into it rather than leaving two rows
 * APPROVED. It means "passed review, was live, has since been replaced" — history, not a
 * defect, and explicitly NOT usable. See `MessageTemplateMaster.STATUS_SUPERSEDED`.
 *
 * `PUBLISHED`/`ARCHIVED` added for Version History (Comms PR-22) — the entity's
 * `chk_template_status` vocabulary widened to these 8 values in PR-18/PR-20 (schema
 * V1_5_0_0_88); this type had not been updated to match until this PR needed to render
 * them as status badges.
 */
export type TemplateStatus =
  | 'DRAFT'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'SUPERSEDED'
  | 'REJECTED'
  | 'CHANGES_REQUESTED'
  | 'ARCHIVED';

/**
 * HOW a template is dispatched — mirrors the backend `TemplateKind` enum (`template_kind`,
 * V1_5_3_6_8), persisted as the constant NAME in a VARCHAR(16) guarded by `chk_template_kind`.
 *
 * ORTHOGONAL to {@link TemplateSummary.templateCategory}, which says WHAT the message is about
 * (ESTIMATE_LINK / PAYMENT_LINK / INVOICE / CONTRACT_*). One field cannot carry both: the same
 * CONTRACT template may exist three times over — once fired by the platform, once by an automation
 * plan step, once by a user pressing send — same category, different kind each time. That is why
 * this is a second axis here rather than more members of the category vocabulary.
 */
export type TemplateKind = 'SYSTEM' | 'AUTOMATION' | 'MANUAL';

/**
 * Reader-facing wording for a {@link TemplateKind}. Shared by the row chip and the kind filter so
 * the two can never drift apart. Falls back to the raw value for a kind added server-side that this
 * frontend does not know yet — showing the code is imprecise; guessing a friendly label is wrong.
 */
export const templateKindLabel = (kind: string | null | undefined): string => {
  switch (kind) {
    case 'SYSTEM':
      return 'System';
    case 'AUTOMATION':
      return 'Automation';
    case 'MANUAL':
      return 'Manual';
    default:
      return String(kind ?? 'Unknown');
  }
};

/** Mirrors `CommunicationChannel` codes. Held as raw ints on the wire. */
export const CHANNEL_EMAIL = 1;
export const CHANNEL_SMS = 2;

export const channelLabel = (channel: number | null | undefined): string => {
  switch (channel) {
    case CHANNEL_EMAIL:
      return 'Email';
    case CHANNEL_SMS:
      return 'SMS';
    default:
      // An unmapped code is shown as-is rather than guessed at — a silent "Email" for
      // an unknown channel would misinform the reviewer.
      return `Unknown channel (${channel ?? 'none'})`;
  }
};

export const statusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'UNDER_REVIEW':
      return 'Under review';
    case 'APPROVED':
      // An approved template has passed review but is NOT live, so it reads as the action it
      // needs, not a resting state.
      return 'Ready to publish';
    case 'PUBLISHED':
      return 'Published';
    case 'SUPERSEDED':
      return 'Superseded';
    case 'REJECTED':
      return 'Rejected';
    case 'CHANGES_REQUESTED':
      return 'Changes requested';
    case 'ARCHIVED':
      return 'Archived';
    default:
      return String(status ?? 'Unknown');
  }
};

/** MUI severity-ish colour key per status, for Chip colouring. */
export const statusColor = (
  status: string | null | undefined,
): 'default' | 'info' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'APPROVED':
      // Amber, not green: "Ready to publish" is a pending action, not a finished/live state.
      return 'warning';
    case 'UNDER_REVIEW':
      return 'info';
    case 'REJECTED':
      return 'error';
    case 'CHANGES_REQUESTED':
      return 'warning';
    case 'SUPERSEDED':
    case 'ARCHIVED':
      // SUPERSEDED shares 'default' with DRAFT deliberately rather than getting a colour of
      // its own: it is inert history that must NOT read as an alert (it is not a failure) and
      // must NOT read as success (nothing may be sent from a superseded body). ARCHIVED is the
      // same shape of inert history for the other terminal, non-live status.
      return 'default';
    default:
      return 'default';
  }
};

/**
 * The request type a reviewer triages on.
 *
 * DERIVED, not fetched — and only because it is honestly derivable. `message_template_master`
 * versions a template by row: version 1 of a `templateMasterUid` is by definition the first
 * one, so it is a brand-new template; any higher version is a revision of the same template.
 * That is a property of the versioning scheme (see `MessageTemplateMaster` javadoc), not a
 * guess.
 *
 * NOTE this is NOT the same thing as `communication_template_change_request.request_kind`,
 * which is a real column on a real table that provider-api does not expose. If that table is
 * ever surfaced, prefer its value over this derivation.
 */
export type RequestType = 'NEW_TEMPLATE' | 'REVISION';

export const requestTypeOf = (version: number | null | undefined): RequestType =>
  (version ?? 1) <= 1 ? 'NEW_TEMPLATE' : 'REVISION';

export const requestTypeLabel = (version: number | null | undefined): string =>
  requestTypeOf(version) === 'NEW_TEMPLATE' ? 'New template' : `Revision → v${version}`;

/**
 * UTC-first date rendering (repo standard).
 *
 * The wire value comes from a Java `OffsetDateTime`, so it already carries a real offset.
 * We parse it as-is and let `Intl` render it in the VIEWER's browser timezone in 12-hour
 * form. We never append or strip a literal `Z`, and never read raw `.getHours()` off it.
 *
 * Returns null for null/blank/unparseable input so callers can render "—" or an explicit
 * "unreadable date" rather than the string "Invalid Date".
 */
export const formatUtcDateTime = (iso: string | null | undefined): string | null => {
  if (iso == null || iso.trim() === '') return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(parsed);
};

/**
 * Groups templates by channel then status, each group sorted by name. Pure.
 *
 * NO LONGER the Template List's layout (2026-08-27): that screen flattened its per-status sections
 * on 2026-08-06 and now orders rows through {@link sortForManageList}. This function is retained,
 * exported and tested for any caller that genuinely wants channel/status grouping; nothing in
 * production calls it today.
 */
export interface TemplateGroup {
  channel: number;
  channelName: string;
  status: TemplateStatus;
  statusName: string;
  templates: TemplateSummary[];
}

/**
 * Mirrors `TemplateTenantUsageDTO` — one tenant's line item in a `TemplateUsage` breakdown.
 */
export interface TemplateTenantUsage {
  tenantId: string;
  referenceCount: number;
}

/**
 * Mirrors `TemplateUsageDTO` (PR-09g) EXACTLY as the wire delivers it — the materialized
 * cross-tenant usage snapshot for one template family, not a live count.
 *
 * `refreshedAt` is the OLDEST `refreshed_at` across every row (provider + every tenant), so a
 * tenant whose count failed to refresh on a recent scheduler pass is never hidden behind a
 * fresher sibling row; `null` only when no refresh has ever run for this template yet.
 */
export interface TemplateUsage {
  templateMasterUid: string;
  providerReferenceCount: number;
  totalReferenceCount: number;
  /** One entry per tenant with a snapshot row, ordered by tenantId ascending (server-sorted). */
  tenantUsage: TemplateTenantUsage[];
  /** ISO-8601 WITH offset, from an OffsetDateTime. Never hand-append or strip a `Z`. Null = never refreshed. */
  refreshedAt: string | null;
}

/**
 * Group ordering within a channel: most-actionable first, inert history last.
 *
 * MUST list every member of {@link TemplateStatus}. `Array.indexOf` returns -1 for a value
 * that is absent, and -1 sorts BEFORE index 0 — so an omitted status does not fall to the
 * bottom, it jumps to the very top. That is exactly what T59 was: `SUPERSEDED` was missing,
 * so a group of dead, unsendable versions rendered above the review queue. `PUBLISHED` and
 * `ARCHIVED` are added here for the same reason, now that both are real members of
 * {@link TemplateStatus} (see that type's own javadoc).
 *
 * `statusRank` below closes the same trap for any status added server-side in future: an
 * unranked value is pushed LAST rather than first. Adding it here is still the fix — the
 * fallback keeps a stale frontend merely imprecise instead of actively misleading.
 */
const STATUS_ORDER: TemplateStatus[] = [
  'UNDER_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'CHANGES_REQUESTED',
  'DRAFT',
  'REJECTED',
  'SUPERSEDED',
  'ARCHIVED',
];

const statusRank = (status: string): number => {
  const index = STATUS_ORDER.indexOf(status as TemplateStatus);
  return index === -1 ? STATUS_ORDER.length : index;
};

export const groupByChannelAndStatus = (templates: TemplateSummary[]): TemplateGroup[] => {
  const keyed = new Map<string, TemplateGroup>();
  templates.forEach((template) => {
    const key = `${template.channel}::${template.status}`;
    if (!keyed.has(key)) {
      keyed.set(key, {
        channel: template.channel,
        channelName: channelLabel(template.channel),
        status: template.status,
        statusName: statusLabel(template.status),
        templates: [],
      });
    }
    keyed.get(key)!.templates.push(template);
  });

  return Array.from(keyed.values())
    .map((group) => ({
      ...group,
      templates: [...group.templates].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.channel !== b.channel) return a.channel - b.channel;
      return statusRank(a.status) - statusRank(b.status);
    });
};

/**
 * Row order for the provider-admin "manage templates" table — NEWEST FIRST, by template family.
 *
 * WHY THIS EXISTS instead of {@link groupByChannelAndStatus}'s (statusRank, name) order:
 * A newly created template is a DRAFT, and `STATUS_ORDER` ranks DRAFT 5th of 6 live statuses, so
 * every new template was pushed behind every UNDER_REVIEW / APPROVED / PUBLISHED /
 * CHANGES_REQUESTED row and then placed alphabetically inside that block — routinely landing on
 * page 2 or 3 of a paginated table. "Where is the template I just made?" had no good answer.
 *
 * Status no longer needs to drive position: the 2026-08-06 redesign flattened the per-status card
 * sections into one table in which every row already carries its own Status chip, so ranking by
 * status became a leftover of a layout that no longer exists.
 *
 * BY FAMILY, not by row:
 * This list can hold several versions of one family at once (a DRAFT v2 beside its PUBLISHED v1 —
 * SUPERSEDED rows are excluded server-side). Sorting rows by `createdAt` alone would scatter those
 * apart, since v1 may be months older. So families are ordered by their newest row and each
 * family's versions travel together, newest version first — which keeps the one genuinely useful
 * property the old `name ASC, version DESC` order had.
 *
 * Ties and gaps are resolved deliberately, never left to array order:
 *  - a missing `createdAt` (optional on the type; null on rows predating Comms PR-22) sorts LAST,
 *    because treating "unknown" as "newest" would park legacy rows permanently at the top;
 *  - families with identical timestamps fall back to name, then to `templateMasterUid`, so the
 *    order is fully deterministic and cannot reshuffle between reloads.
 *
 * Sorts only — never filters. The caller applies the channel filter and the search box.
 */
export const sortForManageList = (templates: TemplateSummary[]): TemplateSummary[] => {
  const families = new Map<string, TemplateSummary[]>();
  templates.forEach((template) => {
    const key = template.templateMasterUid;
    if (!families.has(key)) {
      families.set(key, []);
    }
    families.get(key)!.push(template);
  });

  // Compared as INSTANTS, never as strings. `createdAt` is "ISO-8601 WITH offset" (see the field's
  // own doc) and Jackson omits trailing zeros, so both `…T10:00:00Z` and `…T10:00:00.000001Z` occur,
  // as can `+05:30`. A string compare hits '.' (0x2E) vs 'Z' (0x5A) and calls the LATER instant
  // older, and reads `+05:30` as newer than an earlier `Z` — recorded lesson L-GEN-008.
  // NaN (absent or unparseable) is the "unknown" sentinel and is forced last below.
  const instantOf = (raw: string | null | undefined): number =>
    raw == null ? NaN : Date.parse(raw);
  const newestOf = (rows: TemplateSummary[]): number =>
    rows.reduce((newest, r) => {
      const t = instantOf(r.createdAt);
      return Number.isNaN(t) ? newest : Number.isNaN(newest) ? t : Math.max(newest, t);
    }, NaN);

  return Array.from(families.values())
    .map((rows) => {
      // Newest version FIRST, so [0] is the row whose name the user actually reads in the top line —
      // which is what the name tie-break below must sort on. Reading the unsorted parameter's [0]
      // instead would key on whichever row the API happened to return first, so a family renamed at
      // v2 sorted under its old v1 name while displaying the new one.
      const byVersionDesc = [...rows].sort((a, b) => b.version - a.version);
      return {
        rows: byVersionDesc,
        newest: newestOf(rows),
        name: byVersionDesc[0].name ?? '',
        // Final tie-break: the family key. Without it, two families sharing both timestamp and name
        // fall back to server arrival order, and this query has no id tiebreaker of its own — so
        // Postgres could return them differently per plan and the list would reshuffle on reload.
        uid: byVersionDesc[0].templateMasterUid,
      };
    })
    .sort((a, b) => {
      const aUnknown = Number.isNaN(a.newest);
      const bUnknown = Number.isNaN(b.newest);
      // An unknown timestamp always loses to a real one, whichever side it is on.
      if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
      if (!aUnknown && a.newest !== b.newest) return b.newest - a.newest;
      const byName = a.name.localeCompare(b.name);
      return byName !== 0 ? byName : a.uid.localeCompare(b.uid);
    })
    .flatMap((family) => family.rows);
};

/**
 * Response of POST /message-templates/{uid}/versions/{version}/preview — the server-rendered
 * preview (sample-token substitution + EmailSanitizer for email; segment info for SMS).
 * `renderedBody` is safe, sanitized HTML for email and plain text for SMS.
 */
export interface PreviewResponse {
  subject: string | null;
  renderedBody: string;
  resolvedTokens?: string[];
  unresolvedTokens?: string[];
  segmentCount?: number | null;
  encoding?: string | null;
  segmentCountWarning?: string | null;
  emailViolations?: string[] | null;
}
